"use client"

import { useMemo } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { subDays, format } from "date-fns"
import { RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { getMarketIndicators, collectIntradayMarketIndicators } from "@/lib/api/market-status"
import { pivotByMarket, pivotInvestorTrend, pivotSingleSeries, computeRatio } from "@/lib/market-status/pivot"
import { useViewMode } from "@/lib/market-status/useViewMode"
import AdvanceDeclineChart, { type AdvanceDeclinePoint } from "@/components/market-status/AdvanceDeclineChart"
import IndicatorLineChart from "@/components/market-status/IndicatorLineChart"
import InvestorTrendChart from "@/components/market-status/InvestorTrendChart"
import IndicatorTable from "@/components/market-status/IndicatorTable"
import ViewToggle from "@/components/market-status/ViewToggle"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

const DAYS = 60

function toAdvanceDeclinePoints(
  rows: { indicatorDate: string; value: number; extra: Record<string, number> | null }[]
): AdvanceDeclinePoint[] {
  return rows.map((r) => ({
    date: r.indicatorDate.slice(5),
    advance: r.value,
    decline: r.extra?.decline ?? 0,
  }))
}

interface AdvanceDeclineTableRow {
  date: string
  advance: number
  decline: number
  unchanged: number
}

function toAdvanceDeclineTableRows(
  rows: { indicatorDate: string; value: number; extra: Record<string, number> | null }[]
): AdvanceDeclineTableRow[] {
  return rows.map((r) => ({
    date: r.indicatorDate.slice(5),
    advance: r.value,
    decline: r.extra?.decline ?? 0,
    unchanged: r.extra?.unchanged ?? 0,
  }))
}

function useIndicator(type: string, from: string) {
  return useQuery({
    queryKey: ["market-indicators", type, from],
    queryFn: () => getMarketIndicators({ type, from }),
  })
}

export default function MarketStatusPage() {
  const from = useMemo(() => format(subDays(new Date(), DAYS), "yyyy-MM-dd"), [])

  const tradingValue = useIndicator("trading_value", from)
  const creditBalance = useIndicator("credit_balance", from)
  const investorTrend = useIndicator("investor_trend", from)
  const advanceDecline = useIndicator("advance_decline", from)
  const lendingBalance = useIndicator("lending_balance", from)
  const futuresInvestorTrend = useIndicator("futures_investor_trend", from)

  const tradingValueSeries = useMemo(
    () => pivotByMarket(tradingValue.data ?? [], { kospi: "kospi", kosdaq: "kosdaq" }),
    [tradingValue.data]
  )
  const ratioSeries = useMemo(() => computeRatio(tradingValueSeries, "kospi", "kosdaq"), [tradingValueSeries])

  const creditBalanceSeries = useMemo(() => pivotSingleSeries(creditBalance.data ?? []), [creditBalance.data])
  const lendingBalanceSeries = useMemo(() => pivotSingleSeries(lendingBalance.data ?? []), [lendingBalance.data])
  const futuresTrendSeries = useMemo(() => pivotInvestorTrend(futuresInvestorTrend.data ?? []), [futuresInvestorTrend.data])

  const investorKospi = useMemo(
    () => pivotInvestorTrend((investorTrend.data ?? []).filter((r) => r.market === "kospi")),
    [investorTrend.data]
  )
  const investorKosdaq = useMemo(
    () => pivotInvestorTrend((investorTrend.data ?? []).filter((r) => r.market === "kosdaq")),
    [investorTrend.data]
  )

  const adKospi = useMemo(
    () => toAdvanceDeclinePoints((advanceDecline.data ?? []).filter((r) => r.market === "kospi")),
    [advanceDecline.data]
  )
  const adKosdaq = useMemo(
    () => toAdvanceDeclinePoints((advanceDecline.data ?? []).filter((r) => r.market === "kosdaq")),
    [advanceDecline.data]
  )
  const adKospiTable = useMemo(
    () => toAdvanceDeclineTableRows((advanceDecline.data ?? []).filter((r) => r.market === "kospi")),
    [advanceDecline.data]
  )
  const adKosdaqTable = useMemo(
    () => toAdvanceDeclineTableRows((advanceDecline.data ?? []).filter((r) => r.market === "kosdaq")),
    [advanceDecline.data]
  )

  const [tradingValueMode, setTradingValueMode] = useViewMode("trading-value", tradingValueSeries.length)
  const [ratioMode, setRatioMode] = useViewMode("ratio", ratioSeries.length)
  const [investorKospiMode, setInvestorKospiMode] = useViewMode("investor-trend-kospi", investorKospi.length)
  const [investorKosdaqMode, setInvestorKosdaqMode] = useViewMode("investor-trend-kosdaq", investorKosdaq.length)
  const [adKospiMode, setAdKospiMode] = useViewMode("advance-decline-kospi", adKospi.length)
  const [adKosdaqMode, setAdKosdaqMode] = useViewMode("advance-decline-kosdaq", adKosdaq.length)
  const [futuresMode, setFuturesMode] = useViewMode("futures-investor-trend", futuresTrendSeries.length)

  return (
    <div className="flex flex-col gap-6 p-6">
      <h1 className="text-xl font-semibold text-zinc-100">시장현황</h1>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>① 거래대금 추이 (최근 {DAYS}일)</CardTitle>
            <CardAction>
              <ViewToggle mode={tradingValueMode} onChange={setTradingValueMode} />
            </CardAction>
          </CardHeader>
          <CardContent>
            {tradingValue.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : tradingValueMode === "chart" ? (
              <IndicatorLineChart
                data={tradingValueSeries}
                lines={[
                  { dataKey: "kospi", name: "코스피(백만원)", color: "#f87171" },
                  { dataKey: "kosdaq", name: "코스닥(백만원)", color: "#60a5fa" },
                ]}
              />
            ) : (
              <IndicatorTable
                data={tradingValueSeries as unknown as { date: string; kospi: number; kosdaq: number }[]}
                columns={[
                  { key: "kospi", label: "코스피(백만원)" },
                  { key: "kosdaq", label: "코스닥(백만원)" },
                ]}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>⑤ 코스피/코스닥 거래대금 비중 (최근 {DAYS}일)</CardTitle>
            <CardAction>
              <ViewToggle mode={ratioMode} onChange={setRatioMode} />
            </CardAction>
          </CardHeader>
          <CardContent>
            {tradingValue.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : ratioMode === "chart" ? (
              <IndicatorLineChart
                data={ratioSeries}
                lines={[
                  { dataKey: "kospiRatio", name: "코스피 비중(%)", color: "#f87171" },
                  { dataKey: "kosdaqRatio", name: "코스닥 비중(%)", color: "#60a5fa" },
                ]}
              />
            ) : (
              <IndicatorTable
                data={ratioSeries as unknown as { date: string; kospiRatio: number; kosdaqRatio: number }[]}
                columns={[
                  { key: "kospiRatio", label: "코스피(%)" },
                  { key: "kosdaqRatio", label: "코스닥(%)" },
                ]}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>② 신용잔고 - 코스닥 신용거래융자 (최근 {DAYS}일)</CardTitle>
          </CardHeader>
          <CardContent>
            {creditBalance.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : (
              <IndicatorLineChart data={creditBalanceSeries} lines={[{ dataKey: "value", name: "신용잔고(백만원)", color: "#fbbf24" }]} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>⑥ 대차거래 잔고 추이 (최근 {DAYS}일)</CardTitle>
          </CardHeader>
          <CardContent>
            {lendingBalance.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : (
              <IndicatorLineChart data={lendingBalanceSeries} lines={[{ dataKey: "value", name: "대차잔고(백만원)", color: "#a78bfa" }]} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>③ 투자자매매동향 - 코스피 (최근 {DAYS}일)</CardTitle>
            <CardAction>
              <ViewToggle mode={investorKospiMode} onChange={setInvestorKospiMode} />
            </CardAction>
          </CardHeader>
          <CardContent>
            {investorTrend.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : investorKospiMode === "chart" ? (
              <InvestorTrendChart data={investorKospi} />
            ) : (
              <IndicatorTable
                data={investorKospi}
                columns={[
                  { key: "individual", label: "개인(백만원)" },
                  { key: "foreign", label: "외국인(백만원)" },
                  { key: "institution", label: "기관계(백만원)" },
                ]}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>③ 투자자매매동향 - 코스닥 (최근 {DAYS}일)</CardTitle>
            <CardAction>
              <ViewToggle mode={investorKosdaqMode} onChange={setInvestorKosdaqMode} />
            </CardAction>
          </CardHeader>
          <CardContent>
            {investorTrend.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : investorKosdaqMode === "chart" ? (
              <InvestorTrendChart data={investorKosdaq} />
            ) : (
              <IndicatorTable
                data={investorKosdaq}
                columns={[
                  { key: "individual", label: "개인(백만원)" },
                  { key: "foreign", label: "외국인(백만원)" },
                  { key: "institution", label: "기관계(백만원)" },
                ]}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>④ 코스피 등락 종목수 (최근 {DAYS}일)</CardTitle>
            <CardAction>
              <ViewToggle mode={adKospiMode} onChange={setAdKospiMode} />
            </CardAction>
          </CardHeader>
          <CardContent>
            {advanceDecline.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : adKospiMode === "chart" ? (
              <AdvanceDeclineChart data={adKospi} />
            ) : (
              <IndicatorTable
                data={adKospiTable}
                columns={[
                  { key: "advance", label: "상승" },
                  { key: "unchanged", label: "보합" },
                  { key: "decline", label: "하락" },
                ]}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>④ 코스닥 등락 종목수 (최근 {DAYS}일)</CardTitle>
            <CardAction>
              <ViewToggle mode={adKosdaqMode} onChange={setAdKosdaqMode} />
            </CardAction>
          </CardHeader>
          <CardContent>
            {advanceDecline.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : adKosdaqMode === "chart" ? (
              <AdvanceDeclineChart data={adKosdaq} />
            ) : (
              <IndicatorTable
                data={adKosdaqTable}
                columns={[
                  { key: "advance", label: "상승" },
                  { key: "unchanged", label: "보합" },
                  { key: "decline", label: "하락" },
                ]}
              />
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>⑦ 코스피200 선물 투자자매매동향 (최근 {DAYS}일)</CardTitle>
            <CardAction>
              <ViewToggle mode={futuresMode} onChange={setFuturesMode} />
            </CardAction>
          </CardHeader>
          <CardContent>
            {futuresInvestorTrend.isLoading ? (
              <Skeleton className="h-[240px] w-full" />
            ) : futuresMode === "chart" ? (
              <InvestorTrendChart data={futuresTrendSeries} />
            ) : (
              <IndicatorTable
                data={futuresTrendSeries}
                columns={[
                  { key: "individual", label: "개인(백만원)" },
                  { key: "foreign", label: "외국인(백만원)" },
                  { key: "institution", label: "기관계(백만원)" },
                ]}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
